import { useCallback, useEffect, useRef, useState } from "react";

import {
  ExclamationTriangleIcon,
  LinkIcon,
  LinkSlashIcon,
  MagnifyingGlassIcon,
  UserPlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";

import apiService from "../../services/api";
import ModalShell from "../ModalShell";
import { Shimmer } from "../MotionPrimitives";
import ResellerManagedBadge from "../ResellerManagedBadge";

const MANAGED_LINK_NOTE_MAX_LENGTH = 500;

const formatDate = (value) => {
  if (!value) return "Not available";
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const titleCase = (value) =>
  String(value || "unknown")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const displayName = (registration) =>
  registration?.name || registration?.email || registration?.registrationId;

const RegistrationStatus = ({ status }) => {
  const isActive = String(status || "").toUpperCase() === "ACTIVE";

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
        isActive
          ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
          : "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
      }`}
    >
      {titleCase(status)}
    </span>
  );
};

const Notice = ({ children, tone = "amber" }) => (
  <div
    className={`flex items-start gap-2 rounded-lg border p-3 text-sm ${
      tone === "red"
        ? "border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300"
        : "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200"
    }`}
  >
    <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />
    <div>{children}</div>
  </div>
);

const NEUTRAL_BADGE_CLASSES =
  "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300";

const LINK_STATUS_BADGES = {
  active: {
    className:
      "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
    label: "Active",
  },
  inactive: { className: NEUTRAL_BADGE_CLASSES, label: "Inactive" },
  needs_review: {
    className:
      "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
    label: "Needs review",
  },
};

/**
 * Shows whether a managed link currently grants access, with the API's
 * human-readable reason when it does not.
 *
 * @param {{ status?: "active"|"inactive"|"needs_review", statusReason?: string|null }} props
 * @returns {import("react").ReactElement|null}
 */
const LinkStatus = ({ status, statusReason }) => {
  if (!status && !statusReason) return null;

  const badge = LINK_STATUS_BADGES[status] || {
    className: NEUTRAL_BADGE_CLASSES,
    label: titleCase(status),
  };

  return (
    <div className="flex flex-col items-start gap-1">
      <span
        className={`inline-flex items-center whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-medium ${badge.className}`}
      >
        {badge.label}
      </span>
      {statusReason && (
        <p className="max-w-[14rem] text-xs text-gray-500 dark:text-gray-400 break-words">
          {statusReason}
        </p>
      )}
    </div>
  );
};

const CandidatePreview = ({ candidate, canCreateLinks }) => {
  if (candidate === undefined) return null;

  if (candidate === null) {
    return (
      <p className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600 dark:border-white/10 dark:bg-white/5 dark:text-gray-300">
        No dashboard registration matches that exact email or registration ID.
      </p>
    );
  }

  const canLink =
    canCreateLinks && candidate.eligible && !candidate.alreadyLinked;

  return (
    <div className="rounded-lg border border-gray-200 p-3 dark:border-white/10">
      <div className="flex flex-wrap items-center gap-2">
        <p className="font-semibold text-gray-900 dark:text-white break-all">
          {displayName(candidate)}
        </p>
        <RegistrationStatus status={candidate.registrationStatus} />
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400 break-all">
        {candidate.email}
      </p>
      <p className="mt-1 font-mono text-xs text-gray-400 break-all">
        Registration {candidate.registrationId}
      </p>
      <p
        className={`mt-2 text-sm font-medium ${
          canLink
            ? "text-green-700 dark:text-green-300"
            : "text-red-600 dark:text-red-300"
        }`}
      >
        {candidate.alreadyLinked
          ? "Already linked to this playlist."
          : candidate.eligible
            ? canCreateLinks
              ? "Eligible for reseller-managed access."
              : "Linking is unavailable for this playlist right now."
            : candidate.ineligibleReason ||
              "Not eligible for reseller-managed access."}
      </p>
    </div>
  );
};

/**
 * Admin panel for granting customers read-only dashboard access to a
 * playlist that an active reseller manages.
 *
 * @param {{ userId: string, onOpenCustomer: (registrationId: string) => void }} props
 * @returns {import("react").ReactElement}
 */
const ResellerManagedAccessPanel = ({ userId, onOpenCustomer }) => {
  const [state, setState] = useState({
    data: null,
    error: null,
    loading: true,
  });
  const requestSequenceRef = useRef(0);
  const lookupSequenceRef = useRef(0);
  const [pendingUnlink, setPendingUnlink] = useState(null);
  const [isUnlinking, setIsUnlinking] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [lookupQuery, setLookupQuery] = useState("");
  const [candidate, setCandidate] = useState(undefined);
  const [isSearching, setIsSearching] = useState(false);
  const [note, setNote] = useState("");
  const [isSubmittingLink, setIsSubmittingLink] = useState(false);
  const lookupInputRef = useRef(null);
  const unlinkCancelRef = useRef(null);

  const loadLinks = useCallback(async () => {
    const sequence = requestSequenceRef.current + 1;

    requestSequenceRef.current = sequence;
    setState((current) => ({ ...current, error: null, loading: true }));

    try {
      const response = await apiService.getPlaylistManagedLinks(userId);

      if (requestSequenceRef.current !== sequence) return;
      setState({ data: response?.data || null, error: null, loading: false });
    } catch (error) {
      if (requestSequenceRef.current !== sequence) return;
      setState((current) => ({
        ...current,
        error: error?.message || "Failed to load reseller-managed access.",
        loading: false,
      }));
    }
  }, [userId]);

  useEffect(() => {
    setState({ data: null, error: null, loading: true });
    loadLinks();

    return () => {
      requestSequenceRef.current += 1;
      lookupSequenceRef.current += 1;
    };
  }, [loadLinks]);

  const { data } = state;
  const manager = data?.manager || null;
  const links = data?.links || [];
  const suggestions = data?.suggestions || [];
  const isRemoved = data?.playlist?.removed === true;
  const canCreateLinks = Boolean(manager) && !isRemoved;

  const createLink = async (registrationId, linkNote) => {
    const response = await apiService.createPlaylistManagedLink(userId, {
      registrationId,
      note: linkNote,
    });

    if (response?.data?.created === false) {
      toast.info("This customer is already linked to this playlist.");
    } else {
      toast.success("Reseller-managed access linked.");
    }

    await loadLinks();
  };

  // Abandons any in-flight lookup so its response can no longer set state.
  const invalidateLookup = () => {
    lookupSequenceRef.current += 1;
    setIsSearching(false);
  };

  /**
   * Opens the Link modal empty, or prefilled with a suggested registration so
   * the admin reviews the same preview and confirms before anything is linked.
   *
   * @param {object} [suggestion]
   */
  const openLinkModal = (suggestion) => {
    invalidateLookup();
    setLookupQuery(
      suggestion ? suggestion.email || suggestion.registrationId || "" : ""
    );
    setCandidate(
      suggestion
        ? { ...suggestion, alreadyLinked: suggestion.alreadyLinked === true }
        : undefined
    );
    setNote("");
    setShowLinkModal(true);
  };

  const closeLinkModal = () => {
    if (isSubmittingLink) return;
    invalidateLookup();
    setShowLinkModal(false);
  };

  const handleLookup = async (event) => {
    event.preventDefault();
    const query = lookupQuery.trim();

    if (!query || isSearching) return;

    const sequence = lookupSequenceRef.current + 1;

    lookupSequenceRef.current = sequence;
    setIsSearching(true);
    setCandidate(undefined);
    try {
      const response = await apiService.findPlaylistManagedLinkCandidate(
        userId,
        query
      );

      if (lookupSequenceRef.current !== sequence) return;
      setCandidate(response?.data?.candidate ?? null);
    } catch (error) {
      if (lookupSequenceRef.current !== sequence) return;
      toast.error(error?.message || "Failed to look up that customer.");
    } finally {
      if (lookupSequenceRef.current === sequence) setIsSearching(false);
    }
  };

  const canSubmitLink =
    canCreateLinks &&
    Boolean(candidate?.eligible) &&
    !candidate?.alreadyLinked &&
    note.length <= MANAGED_LINK_NOTE_MAX_LENGTH &&
    !isSubmittingLink;

  const handleSubmitLink = async () => {
    if (!canSubmitLink) return;

    setIsSubmittingLink(true);
    try {
      await createLink(candidate.registrationId, note.trim() || undefined);
      setShowLinkModal(false);
    } catch (error) {
      toast.error(error?.message || "Failed to link this customer.");
      // The failure may mean the panel is stale (link already exists,
      // eligibility changed, playlist removed), so refresh what it shows.
      loadLinks();
    } finally {
      setIsSubmittingLink(false);
    }
  };

  const handleConfirmUnlink = async () => {
    if (!pendingUnlink || isUnlinking) return;

    setIsUnlinking(true);
    try {
      await apiService.revokePlaylistManagedLink(userId, pendingUnlink.linkId);
      toast.success("Reseller-managed access removed.");
      setPendingUnlink(null);
      await loadLinks();
    } catch (error) {
      toast.error(error?.message || "Failed to unlink this customer.");
      // The link may already be gone, so close the confirmation and refresh
      // the list instead of leaving a stale Unlink target on screen.
      setPendingUnlink(null);
      loadLinks();
    } finally {
      setIsUnlinking(false);
    }
  };

  const renderBody = () => {
    if (state.error && !data) {
      return (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300 flex items-center justify-between gap-3">
          <span>{state.error}</span>
          <button
            className="font-semibold hover:underline"
            type="button"
            onClick={loadLinks}
          >
            Retry
          </button>
        </div>
      );
    }

    if (!data) return <Shimmer className="h-24 rounded-xl" />;

    return (
      <div className="space-y-6">
        {state.error && (
          <Notice tone="red">
            {state.error}{" "}
            <button
              className="font-semibold hover:underline"
              type="button"
              onClick={loadLinks}
            >
              Retry
            </button>
          </Notice>
        )}

        <section>
          <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
            Current reseller manager
          </h3>
          {isRemoved && (
            <div className="mb-3">
              <Notice tone="red">
                This playlist has been removed. Linked customers no longer see
                it and new links cannot be created.
              </Notice>
            </div>
          )}
          {manager ? (
            <dl className="grid gap-x-6 gap-y-3 rounded-xl border border-gray-200 p-4 text-sm dark:border-gray-700 sm:grid-cols-2 lg:grid-cols-3">
              {[
                { label: "Reseller", value: manager.name || "Not provided" },
                { label: "Email", value: manager.email || "Not provided" },
                {
                  label: "Reseller type",
                  value: titleCase(manager.resellerType || "regular"),
                },
                { label: "LunaKey", mono: true, value: manager.code },
                { label: "LunaKey type", value: titleCase(manager.redeemType) },
                {
                  label: "Expires",
                  value: manager.expiresAt
                    ? formatDate(manager.expiresAt)
                    : "Never",
                },
              ].map(({ label, mono, value }) => (
                <div key={label}>
                  <dt className="text-gray-500 dark:text-gray-400">{label}</dt>
                  <dd
                    className={`mt-0.5 break-all text-gray-900 dark:text-gray-100 ${
                      mono ? "font-mono text-xs" : "font-medium"
                    }`}
                  >
                    {value || "Not available"}
                  </dd>
                </div>
              ))}
            </dl>
          ) : (
            <Notice>
              <p className="font-semibold">No active reseller LunaKey</p>
              <p className="mt-1">
                This playlist has no active LunaKey owned by an active reseller,
                so dashboard customers cannot be linked to it. Existing links
                stay visible to their customers as inactive and read-only.
              </p>
            </Notice>
          )}
        </section>

        <section>
          <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
            Linked dashboard customers
          </h3>
          {links.length === 0 ? (
            <p className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">
              No dashboard customers are linked to this playlist.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    <th className="pb-3 pr-4 font-medium">Customer</th>
                    <th className="pb-3 pr-4 font-medium">Status</th>
                    <th className="pb-3 pr-4 font-medium">Link status</th>
                    <th className="pb-3 pr-4 font-medium">Note</th>
                    <th className="pb-3 pr-4 font-medium">Linked</th>
                    <th className="pb-3 font-medium">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700/70">
                  {links.map((link) => (
                    <tr key={link.linkId}>
                      <td className="py-3 pr-4">
                        <button
                          className="text-left font-semibold text-primary-600 dark:text-primary-400 hover:underline break-all"
                          type="button"
                          onClick={() => onOpenCustomer(link.registrationId)}
                        >
                          {displayName(link)}
                        </button>
                        <p className="text-xs text-gray-500 dark:text-gray-400 break-all">
                          {link.email}
                        </p>
                      </td>
                      <td className="py-3 pr-4">
                        <RegistrationStatus status={link.registrationStatus} />
                      </td>
                      <td className="py-3 pr-4">
                        <LinkStatus
                          status={link.status}
                          statusReason={link.statusReason}
                        />
                      </td>
                      <td className="py-3 pr-4 max-w-xs text-gray-700 dark:text-gray-300 break-words">
                        {link.note || (
                          <span className="text-gray-400 dark:text-gray-500">
                            No note
                          </span>
                        )}
                      </td>
                      <td className="py-3 pr-4 whitespace-nowrap text-gray-500 dark:text-gray-400">
                        {formatDate(link.createdAt)}
                        <p className="text-xs">
                          by {link.createdBy?.email || "Unknown admin"}
                        </p>
                      </td>
                      <td className="py-3 text-right">
                        <button
                          className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-900/20"
                          type="button"
                          onClick={() => setPendingUnlink(link)}
                        >
                          <LinkSlashIcon className="h-4 w-4" />
                          Unlink
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {suggestions.length > 0 && (
          <section>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              Suggested customers
            </h3>
            <p className="mt-1 mb-3 text-xs text-gray-500 dark:text-gray-400">
              Registrations whose legacy playlist pointer references this
              playlist. A pointer alone does not prove ownership, so confirm the
              customer before linking.
            </p>
            <ul className="divide-y divide-gray-100 rounded-xl border border-gray-200 dark:divide-gray-700/70 dark:border-gray-700">
              {suggestions.map((suggestion) => {
                const canLinkSuggestion = canCreateLinks && suggestion.eligible;

                return (
                  <li
                    className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between"
                    key={suggestion.registrationId}
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          className="text-left font-semibold text-primary-600 dark:text-primary-400 hover:underline break-all"
                          type="button"
                          onClick={() =>
                            onOpenCustomer(suggestion.registrationId)
                          }
                        >
                          {displayName(suggestion)}
                        </button>
                        <RegistrationStatus
                          status={suggestion.registrationStatus}
                        />
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                          {suggestion.reason === "legacy_pointer"
                            ? "Legacy pointer"
                            : titleCase(suggestion.reason)}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 break-all">
                        {suggestion.email}
                      </p>
                      {!suggestion.eligible && (
                        <p className="mt-1 text-xs text-red-600 dark:text-red-300">
                          {suggestion.ineligibleReason ||
                            "Not eligible for reseller-managed access."}
                        </p>
                      )}
                    </div>
                    {canLinkSuggestion && (
                      <button
                        className="btn btn-primary btn-sm inline-flex flex-shrink-0 items-center justify-center gap-1.5"
                        type="button"
                        onClick={() => openLinkModal(suggestion)}
                      >
                        <LinkIcon className="h-4 w-4" />
                        Link
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        )}
      </div>
    );
  };

  return (
    <section className="card rounded-xl shadow-md overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">
              Reseller-Managed Dashboard Access
            </h2>
            <ResellerManagedBadge size="sm" />
          </div>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Let a customer&apos;s own dashboard account view this playlist
            read-only while the reseller keeps full control.
          </p>
        </div>
        <div className="flex flex-col items-start gap-1 sm:items-end">
          <button
            className="btn btn-primary inline-flex flex-shrink-0 items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!canCreateLinks}
            title={
              canCreateLinks
                ? undefined
                : "Linking requires an active reseller LunaKey on this playlist"
            }
            type="button"
            onClick={() => openLinkModal()}
          >
            <UserPlusIcon className="h-4 w-4" />
            Link dashboard customer
          </button>
          {data && !canCreateLinks && (
            <p className="max-w-xs text-xs text-gray-500 dark:text-gray-400 sm:text-right">
              {isRemoved
                ? "Removed playlists cannot be linked."
                : "Linking requires an active reseller LunaKey on this playlist."}
            </p>
          )}
        </div>
      </div>
      <div className="p-5">{renderBody()}</div>

      <ModalShell
        initialFocusRef={lookupInputRef}
        isBusy={isSubmittingLink}
        isOpen={showLinkModal}
        labelledBy="managed-link-modal-title"
        onClose={closeLinkModal}
      >
        <div className="flex items-start justify-between gap-4 px-6 pt-6">
          <div>
            <p className="mb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-purple-600 dark:text-purple-300">
              Reseller-managed access
            </p>
            <h3
              className="text-xl font-bold tracking-tight text-gray-900 dark:text-white"
              id="managed-link-modal-title"
            >
              Link dashboard customer
            </h3>
          </div>
          <button
            aria-label="Close"
            className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50 dark:hover:bg-white/10 dark:hover:text-gray-200"
            disabled={isSubmittingLink}
            type="button"
            onClick={closeLinkModal}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 px-6 py-5">
          <form className="space-y-2" onSubmit={handleLookup}>
            <label className="label" htmlFor="managed-link-lookup">
              Customer email or registration ID
            </label>
            <div className="flex gap-2">
              <input
                autoComplete="off"
                className="input"
                id="managed-link-lookup"
                placeholder="customer@example.com"
                ref={lookupInputRef}
                type="text"
                value={lookupQuery}
                onChange={(event) => {
                  invalidateLookup();
                  setLookupQuery(event.target.value);
                  setCandidate(undefined);
                }}
              />
              <button
                className="btn btn-primary flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!lookupQuery.trim() || isSearching}
                type="submit"
              >
                <MagnifyingGlassIcon className="h-4 w-4" />
                {isSearching ? "Finding..." : "Find"}
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Enter the exact email address or registration ID. Partial matches
              are not supported.
            </p>
          </form>

          <CandidatePreview
            canCreateLinks={canCreateLinks}
            candidate={candidate}
          />

          <div>
            <label className="label" htmlFor="managed-link-note">
              Note (optional)
            </label>
            <textarea
              className="input min-h-[80px]"
              id="managed-link-note"
              maxLength={MANAGED_LINK_NOTE_MAX_LENGTH}
              placeholder="For example, the support ticket that requested this link"
              value={note}
              onChange={(event) =>
                setNote(
                  event.target.value.slice(0, MANAGED_LINK_NOTE_MAX_LENGTH)
                )
              }
            />
            <p className="mt-1 text-right text-xs tabular-nums text-gray-500 dark:text-gray-400">
              {note.length}/{MANAGED_LINK_NOTE_MAX_LENGTH}
            </p>
          </div>
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-gray-100 bg-gray-50/95 px-6 py-4 dark:border-white/10 dark:bg-gray-900/95 sm:flex-row sm:justify-end">
          <button
            className="btn btn-ghost justify-center disabled:opacity-50"
            disabled={isSubmittingLink}
            type="button"
            onClick={closeLinkModal}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!canSubmitLink}
            type="button"
            onClick={handleSubmitLink}
          >
            <LinkIcon className="h-4 w-4" />
            {isSubmittingLink ? "Linking..." : "Link customer"}
          </button>
        </div>
      </ModalShell>

      <ModalShell
        initialFocusRef={unlinkCancelRef}
        isBusy={isUnlinking}
        isOpen={Boolean(pendingUnlink)}
        labelledBy="managed-unlink-modal-title"
        onClose={() => {
          if (!isUnlinking) setPendingUnlink(null);
        }}
      >
        <div className="px-6 pb-5 pt-6">
          <h3
            className="text-xl font-bold tracking-tight text-gray-900 dark:text-white"
            id="managed-unlink-modal-title"
          >
            Unlink this customer?
          </h3>
          <p className="mt-3 text-sm leading-6 text-gray-600 dark:text-gray-300">
            <span className="font-semibold break-all">
              {displayName(pendingUnlink)}
            </span>{" "}
            will immediately lose read-only dashboard access to this playlist
            and any Premium status granted through it. The reseller keeps full
            control of the playlist.
          </p>
        </div>
        <div className="flex flex-col-reverse gap-2 border-t border-gray-100 bg-gray-50/95 px-6 py-4 dark:border-white/10 dark:bg-gray-900/95 sm:flex-row sm:justify-end">
          <button
            className="btn btn-ghost justify-center disabled:opacity-50"
            disabled={isUnlinking}
            ref={unlinkCancelRef}
            type="button"
            onClick={() => setPendingUnlink(null)}
          >
            Cancel
          </button>
          <button
            className="btn justify-center rounded-full bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isUnlinking}
            type="button"
            onClick={handleConfirmUnlink}
          >
            <LinkSlashIcon className="h-4 w-4" />
            {isUnlinking ? "Unlinking..." : "Unlink"}
          </button>
        </div>
      </ModalShell>
    </section>
  );
};

export default ResellerManagedAccessPanel;
